/**
 * 全局配置：统一从 import.meta.env 读取，业务代码不再直接碰环境变量
 */

/**
 * 后端接口根地址
 * - Web 端与站点同源部署，走相对地址（VITE_APP_BASE_API_WEB，默认 /capi），
 *   产物不绑域名，niuren / xingkt 两个站点可共用一份 Web 产物；
 * - 桌面端没有同源概念（file:// 加载），直连 VITE_APP_BASE_API 的绝对地址。
 */
export const APP_BASE_API: string = __WEB__
  ? String(import.meta.env.VITE_APP_BASE_API_WEB || import.meta.env.VITE_APP_BASE_API || '/capi')
  : String(import.meta.env.VITE_APP_BASE_API || '/api')
/** 应用名 */
export const APP_NAME: string = String(import.meta.env.VITE_APP_NAME || '牛人智能')
/**
 * 「AI 主体」的显示名（VITE_APP_AI_IDENTITY 配置，默认 AIIP）
 * 全项目用户可见文案统一用这个变量，以后改名只改环境变量即可
 */
export const AI_IDENTITY_NAME: string = String(import.meta.env.VITE_APP_AI_IDENTITY || 'AIIP')
/** 文件（OSS）根地址 */
export const APP_BASE_FILE_URL: string = String(import.meta.env.VITE_APP_BASE_FILE_URL || '')
/** 站点地址（邀请 / 分享外链前缀） */
export const APP_SITE_URL: string = String(import.meta.env.VITE_APP_SITE_URL || '').replace(/\/+$/, '')
/** 邀请落地页路径 */
export const APP_INVITE_PATH = '/wxxcx/invite'

/**
 * 内容分享落地页路径（**注意带 /h5 前缀**）
 *
 * 微信公众平台「扫普通链接二维码打开小程序」里配的就是这条规则：
 * 用户扫 https://<域名>/h5/wxxcx/aicontent/share?id=xxx → 打开小程序 → pages/content/share。
 * 移动端 utils/share.ts 的 parseShareContentId 负责从二维码原文里取出内容 id。
 */
export const APP_SHARE_PATH = '/h5/wxxcx/aicontent/share'

/** 桌面端 appCode（检查更新用，需与后台「系统管理 - 应用版本」里的 appCode 一致） */
export const DESK_APP_CODE: string = String(import.meta.env.VITE_DESK_APP_CODE || 'niuren-desk')
/** 桌面端平台标识（后台版本记录按 appCode + platform 匹配） */
export const DESK_PLATFORM: string = String(import.meta.env.VITE_DESK_PLATFORM || 'windows')

/** 桌面版安装包在站点上的相对路径（Web 端直接用相对地址下载，不绑域名） */
const DESKTOP_EXE_PATH = '/desk/niuren-win.exe'
/**
 * 桌面版安装包：Windows（.exe）。文件名固定不带版本号，发版时直接覆盖同一个地址
 * - Web 端：与站点同源，用相对地址 /desk/niuren-win.exe；
 * - 桌面端：主进程下载安装包需要绝对地址（「重新下载安装包」兜底），用站点域名拼出来。
 */
export const DESKTOP_DOWNLOAD_URL_WIN: string = String(
  import.meta.env.VITE_DESKTOP_DOWNLOAD_URL_WIN ||
    (__WEB__ ? DESKTOP_EXE_PATH : `${APP_SITE_URL || 'https://nr.xingkt.cn'}${DESKTOP_EXE_PATH}`)
)
/** 桌面版安装包：macOS。Mac 版还在开发中，暂无下载地址（留空即按「开发中」提示） */
export const DESKTOP_DOWNLOAD_URL_MAC: string = String(import.meta.env.VITE_DESKTOP_DOWNLOAD_URL_MAC || '')
/** 桌面版安装包下载地址（旧引用兼容：默认取 Windows 安装包，按系统分流见 utils/desktopDownload.ts） */
export const DESKTOP_DOWNLOAD_URL: string = String(import.meta.env.VITE_DESKTOP_DOWNLOAD_URL || DESKTOP_DOWNLOAD_URL_WIN)
/** 桌面版下载页（用户菜单「下载桌面版」跳到这里，新窗口打开；默认取站点域名下的门户下载锚点） */
export const DESKTOP_SITE_URL: string = String(
  import.meta.env.VITE_DESKTOP_SITE_URL || (APP_SITE_URL ? `${APP_SITE_URL}/#download` : '')
)

/**
 * Web 部署目录前缀（Vite base）
 * 相对路径部署（'./'）时按当前页面路径推导出真实目录（如 /web/），便于拼外链
 */
const rawBase: string = String(import.meta.env.BASE_URL || '/')
export const PUBLISH_PATH: string =
  rawBase.startsWith('.') && typeof location !== 'undefined'
    ? location.pathname.replace(/[^/]*$/, '')
    : rawBase

/** 拼邀请链接 */
export function buildInviteUrl(code: string): string {
  if (!code) return ''
  return `${APP_SITE_URL}${APP_INVITE_PATH}?code=${encodeURIComponent(code)}`
}

/**
 * 拼内容分享地址（二维码内容）：https://<域名>/h5/wxxcx/aicontent/share?id=xxx
 *
 * 微信扫码后直接进小程序的内容分享页（免登录只读），与移动端分享出去的地址同源。
 * APP_SITE_URL 没配（本地调试）时退回当前页面域名。
 */
export function buildContentShareUrl(id: string): string {
  if (!id) return ''
  const site = APP_SITE_URL || (typeof location !== 'undefined' ? location.origin : '')
  return `${site}${APP_SHARE_PATH}?id=${encodeURIComponent(id)}`
}

/** 创作说明 / 生成要求字数上限 */
export const GEN_PROMPT_MAX_LEN = 2000
/** 视频文案字数上限 */
export const VIDEO_TEXT_MAX_LEN = 500
/** 图文附加要求字数上限 */
export const ARTICLE_EXTRA_MAX_LEN = 500
